































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientAdministrationRow, ClientGroup, ClientGroupValue } from "@/data/models/ClientAdministrationRow";
import { listClientsForAdministration, setClientGroup } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  clientGroupError,
  clientGroupsDiffer,
  GroupMemberChoice,
  groupMemberChoices,
  settingsSourceChoices,
  toClientGroupValue,
} from "@/utils/ClientGroup";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";

/** What `show()` resolves with: the saved group, CHANGED_MEANWHILE (the caller reloads), or null. */
export type GroupDialogResult = ClientGroup | "CHANGED_MEANWHILE" | null;

const EMPTY_GROUP: ClientGroup = { isMaster: false, members: [], settingsSource: null, master: null };

/**
 * Edits a client's group (#2610074), in place of IRES Office's MASTER Einstellungen: MASTER-Mandant or
 * not, its members (any Mandant that may join, as IRES Cloud decides it), and the member whose settings
 * it takes over, confirmed before it replaces the master's. The whole group is sent with the one loaded,
 * so IRES Cloud can refuse a change made meanwhile.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientGroupDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: ClientGroup = { ...EMPTY_GROUP };
  form: ClientGroupValue = toClientGroupValue(EMPTY_GROUP);

  isLoadingClients = false;
  clientsLoadFailed = false;
  rows: ClientAdministrationRow[] = [];

  private resolve: ((result: GroupDialogResult) => void) | null = null;

  get yesNoItems() {
    return [
      { text: this.$t("common.yes").toString(), value: true },
      { text: this.$t("common.no").toString(), value: false },
    ];
  }

  get hasStoredMembers(): boolean {
    return this.stored.members.length > 0;
  }

  get memberItems(): GroupMemberChoice[] {
    return groupMemberChoices(
      this.rows,
      this.clientId,
      this.stored,
      this.$t("admin.clients.group.inactive").toString(),
      this.$t("admin.clients.group.deleted").toString()
    );
  }

  get settingsSourceItems() {
    return settingsSourceChoices(this.memberItems, this.form.memberClientIds, EMPTY_VALUE);
  }

  get hasChanged(): boolean {
    return clientGroupsDiffer(toClientGroupValue(this.stored), this.form);
  }

  /** Not a MASTER-Mandant any more: no members, no settings source. */
  @Watch("form.isMaster")
  onIsMasterChanged(isMaster: boolean) {
    if (!isMaster) {
      this.form.memberClientIds = [];
      this.form.settingsSourceClientId = null;
    }
  }

  /** A settings source that is no longer a member is cleared. */
  @Watch("form.memberClientIds")
  onMembersChanged(memberClientIds: string[]) {
    if (this.form.settingsSourceClientId !== null && !memberClientIds.includes(this.form.settingsSourceClientId)) {
      this.form.settingsSourceClientId = null;
    }
  }

  /**
   * @param clientId - the client.
   * @param stored - its group as the detail loaded it.
   */
  show(clientId: string, stored: ClientGroup): Promise<GroupDialogResult> {
    this.clientId = clientId;
    this.stored = { ...stored, members: [...stored.members] };
    this.form = toClientGroupValue(stored);
    this.baseDialog.show({
      title: this.$t("admin.clients.group.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    this.loadClients();
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** Not awaited by `show()`: the current members are offered at once, the rest as the list arrives. */
  async loadClients() {
    const clientId = this.clientId;
    this.isLoadingClients = true;
    this.clientsLoadFailed = false;
    this.rows = [];
    try {
      const list = await listClientsForAdministration();
      // A reopened dialog for another client must not take this answer.
      if (clientId === this.clientId) {
        this.rows = list.clients;
      }
    } catch {
      if (clientId === this.clientId) {
        this.clientsLoadFailed = true;
      }
    } finally {
      this.isLoadingClients = false;
    }
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged) {
      return;
    }
    const source = this.form.settingsSourceClientId;
    // A member's settings replace the master's: said before it happens, as MASTER Einstellungen did.
    if (source !== null && source !== (this.stored.settingsSource?.id ?? null)) {
      const confirmed = await this.confirmDialog.show({
        title: this.$t("admin.clients.group.overwriteTitle").toString(),
        message: this.$t("admin.clients.group.overwriteMessage", [this.nameOf(source)]).toString(),
        acceptMessage: this.$t("admin.clients.group.overwriteAccept").toString(),
        rejectMessage: this.$t("common.back").toString(),
      });
      if (!confirmed) {
        return;
      }
    }

    this.busy = true;
    try {
      const saved = await setClientGroup(this.clientId, toClientGroupValue(this.stored), { ...this.form });
      SnackbarModule.showSuccess({ key: "admin.clients.group.saved" });
      this.close(saved ?? "CHANGED_MEANWHILE");
    } catch (error) {
      const message = clientGroupError(error, (id) => this.nameOf(id));
      SnackbarModule.showError(message);
      if (message.key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  /** A Mandant's name as the picker shows it; the stored group's or the list's, else its id. */
  nameOf(clientId: string): string {
    if (clientId === this.clientId) {
      return this.$t("admin.clients.group.thisClient").toString();
    }
    return this.memberItems.find((item) => item.value === clientId)?.text
      ?? this.rows.find((row) => row.id === clientId)?.clientName
      ?? clientId;
  }

  close(result: GroupDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: GroupDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
